import staticSvr from '@fastify/static'
import fw from '@fastify/websocket'
import { CoreV1Api, CoreV1Event, KubernetesObject, V1Pod } from '@kubernetes/client-node'
import { fork } from 'child_process'
import Fastify, { FastifyInstance, FastifyReply, FastifyRequest } from 'fastify'
import * as yaml from 'js-yaml'
import * as _ from 'lodash'
import { tmpdir } from 'os'
import * as path from 'path'
import { Writable } from 'stream'
import * as WebSocket from 'ws'
import { APP_MODEL_FILES } from 'yam-operator/constants'
import YamStateStore from 'yam-operator/engine/store'
import { axiosClient, createLogger, fsExist, fsMkdir, fsWrite, isMultiEnvClusterMode, lookupWildcardKeyObj, splitAndTrimComma, trimAndLowerAndRemoveSlash, uuid } from 'yam-operator/util'
import { CheckableWorkloadType, IApplicationModel, KV, KVString, OperatorMode } from 'yam-types'
import { Engine } from '..'
import { deleteParameterValues, engineSettings, getParameterValues, listParameterValues, previewTemplate, setParameterValues } from '../ops/engine'
import { createKubeClient4Svr } from '../ops/kube'
import * as packageJson from '../package.json'

const TASK_GC_THRESHOLD = 1800000
const TASK_GC_PERIOD = 60000
const TASK_CALLBACK_URL = process.env.TASK_CALLBACK_URL
const STATIC_SERVE_DIRECTORY = process.env.STATIC_SERVE_DIRECTORY
// all app model must be in this dir if server started with none localhost only
const SECURE_WORK_DIR = process.env.SECURE_SERVER_WORK_DIR?.replace(/\\/g, '/')


const logger = createLogger('server')

// init tmp dir for storing values
const tmpValuesDir = path.join(tmpdir(), '.yam-tmp')
fsExist(tmpValuesDir).then(exists => {
  if (!exists) {
    fsMkdir(tmpValuesDir).catch(err => {
      logger.error(err)
    })
  }
})

const EngineTaskMap = new Map<string, TaskDetail>()
const RealtimeLogConnections = new Map<string, WebSocket[]>()
const StatusConnections = new Map<string, WebSocket[]>()

export type TaskDetail = {
  success: boolean,
  taskId: string,
  finished: boolean,
  operator: string,
  startTime: number,
  logs: string[],
  endTime?: number,
  err?: string,
  abort: () => void
}

const init = async () => {

  const server: FastifyInstance = Fastify({
    requestTimeout: 30000,
    logger
  })
  await server.register(fw, {
    options: { maxPayload: 1048576 * 2 }
  })

  if (STATIC_SERVE_DIRECTORY) {
    if (await fsExist(STATIC_SERVE_DIRECTORY)) {
      const svrPrefix = path.basename(STATIC_SERVE_DIRECTORY)
      logger.info(`start serving '${STATIC_SERVE_DIRECTORY}', with prefix ${svrPrefix}`)
      await server.register(staticSvr, {
        root: STATIC_SERVE_DIRECTORY,
        prefix: '/' + svrPrefix
      })
    } else {
      logger.info(`'${STATIC_SERVE_DIRECTORY}' not exists, static server will not be started. `)
    }
  }

  server.post('/plan', async (request, reply) => {
    await planApply(request, reply, "plan-only")
  })

  server.post('/apply', async (request, reply) => {
    await planApply(request, reply, "plan-apply")
  })

  server.post('/uninstall', async (request, reply) => {
    await planApply(request, reply, "uninstall")
  })

  server.post('/abort/:taskId', async (request) => {
    const taskId = (request.params as KVString).taskId
    if (EngineTaskMap.has(taskId)) {
      const taskDetail = EngineTaskMap.get(taskId)
      if (taskDetail && !taskDetail.finished) {
        taskDetail?.abort()
        taskDetail.finished = true
        return { success: true, msg: "Task aborted" }
      } else {
        return { success: false, msg: `Task has finished at ${taskDetail?.endTime}` }
      }
    } else {
      return { success: false, msg: `The taskId not found: ${taskId}` }
    }
  })

  server.get('/globalValues', async () => {
    return engineSettings.clientConf.globalValues || {}
  })

  server.get('/customValues', async (request) => {
    const { appModel, filename } = request.query as KVString
    checkTargetFilePath(appModel, filename)
    return getParameterValues({ dir: appModel, filename: filename })
  })

  server.get('/values', async (request) => {
    const { appModel, envLocator } = request.query as KVString
    checkTargetFilePath(appModel)
    const { varDefinitions } = await listParameterValues({ dir: appModel, env: envLocator })
    return varDefinitions
  })

  server.get('/schema', async () => {
    logger.info(`fetching engine schema`)
    const { app: appSchema, settings: settingsSchema, catalog: catalogSchema } = await Engine.generateAppAndSettingsSchema()
    return {
      appSchema, settingsSchema, catalogSchema
    }
  })

  server.post('/values', async (request) => {
    if (request.body instanceof Array) {
      // support batch setting with array
      for (const body of request.body) {
        const { appModel, variables, envLocator, filename } = body as KVString
        checkTargetFilePath(appModel, filename)
        await setParameterValues({ dir: appModel, variables, env: envLocator, filename })
      }
    } else {
      const { appModel, variables, envLocator, filename } = request.body as KVString
      checkTargetFilePath(appModel, filename)
      await setParameterValues({ dir: appModel, variables, env: envLocator, filename })
    }
    return { success: true }
  })

  server.delete('/values', async (request) => {
    const { appModel, variables, envLocator, filename } = request.query as KVString
    checkTargetFilePath(appModel, filename)
    await deleteParameterValues({ dir: appModel, variables, env: envLocator, filename })
    return { success: true }
  })

  server.get('/preview', async (request) => {
    const { appModel, paths, envLocator } = request.query as KVString
    const previewFiles = paths.split(',')
    for (const filename of previewFiles) {
      checkTargetFilePath(appModel, filename.trim())
    }
    return previewTemplate({ dir: appModel, filename: paths, env: envLocator })
  })


  server.get('/status/:taskId', async (req, res) => {
    // When WebSocket not available, use fetchLogs flag to control log output retrieving
    const taskId = (req.params as KVString).taskId
    if (EngineTaskMap.has(taskId)) {
      res.code(200).send(_.omit(EngineTaskMap.get(taskId), "abort", (req.query as KV).fetchLogs ? "-" : "logs"))
    } else {
      res.code(404).send('TaskID not exists')
    }
  })

  server.get('/ws/status/:taskId', { websocket: true }, async (connection, req) => {
    const taskId = (req.params as KVString).taskId
    if (EngineTaskMap.has(taskId)) {
      if (!StatusConnections.has(taskId)) {
        StatusConnections.set(taskId, [])
      }
      StatusConnections.get(taskId)?.push(connection.socket)
      const taskDetail = EngineTaskMap.get(taskId)
      // send initial data
      connection.socket.send(JSON.stringify(_.omit(taskDetail, "abort", "logs")))
    } else {
      // WS ErrorCode Refer: https://www.iana.org/assignments/websocket/websocket.xhtml#table-close-code-number-range
      connection.socket.close(1003, `TaskID ${taskId} not exists.`)
    }
  })

  server.get('/ws/logs/:taskId', { websocket: true }, async (connection, req) => {
    const taskId = (req.params as KVString).taskId
    if (EngineTaskMap.has(taskId)) {
      if (!RealtimeLogConnections.has(taskId)) {
        RealtimeLogConnections.set(taskId, [])
      }
      RealtimeLogConnections.get(taskId)?.push(connection.socket)
      const taskDetail = EngineTaskMap.get(taskId)
      // send initial logs
      connection.socket.send(taskDetail?.logs.join('\n'))
    } else {
      connection.socket.close(1003, `TaskID ${taskId} not exists.`)
    }
  })

  server.get('/version', async (_, reply) => {
    reply.code(200).send({ version: packageJson.version, serverTime: new Date().toISOString(), uptime: process.uptime() })
  })

  server.get('/k8s', async (request, reply) => {
    const { cluster, kind, labelSelector, fieldSelector, namespace, name } = request.query as KVString
    if (!cluster || !kind) {
      reply.code(400).send('Invalid Parameter')
      return
    }
    const kubeConfig = fetchKubeConfigFromHeader(request.headers.authorization)
    const kubeClient = await createKubeClient4Svr(cluster, deserializeKubeConfig(cluster, kubeConfig))
    const results: KubernetesObject[] = await kubeClient.find({ kind, name, namespace }, labelSelector, fieldSelector)
    results.forEach(obj => {
      if (obj.metadata) {
        obj.metadata.managedFields = undefined
      }
    })
    logger.info(`queried ${results.length} ${kind} resources from cluster: ${cluster}`)
    return results
  })

  server.get('/app/status', async (request, reply) => {
    const { app, namespace, cluster } = request.query as KVString
    if (!app || !namespace || !cluster) {
      reply.code(400).send('Invalid Parameter')
      return
    }
    const kubeConfig = fetchKubeConfigFromHeader(request.headers.authorization)
    const kubeClient = await createKubeClient4Svr(cluster, deserializeKubeConfig(cluster, kubeConfig))

    const multiEnvMode = isMultiEnvClusterMode(cluster, engineSettings.clientConf.multiEnvModeClusters)

    const multiEnvWorkload = `${app}-${trimAndLowerAndRemoveSlash(cluster).replace(/\//g, '-')}`
    const workload = multiEnvMode
      ? multiEnvWorkload
      : app

    // YamMetadata must have env suffix, Whether it is multi env mode or not
    const yamMetadata = await YamStateStore.loadYamMetadata(multiEnvWorkload, APP_MODEL_FILES, {
      getConf: (c) => kubeClient.getConf({ ...c, namespace })
    })

    if (!yamMetadata) {
      logger.error(`query ${app} in ${namespace}, app model entry not exists`)
      reply.code(404).send('Application Not Found')
      return
    }

    const appModel = yamMetadata.appModel
    if (!appModel.deploy || !(appModel.deploy as KV).type) {
      const result: AggregatedAppStatus = {
        ready: 'N/A',
        appModel,
        appliedResourcesList: yamMetadata.appliedResources,
        lastAppliedAt: yamMetadata.timestamp.toISOString(),
        workload: {},
        podStatus: [],
        abnormalEvents: {}
      }
      logger.info(`No managed workload in app model, skip fetching Pods, app: '${app}' in namespace: '${namespace}'`)
      return result
    }

    let ready: AppStatus = 'Ready'
    const abnormalEvents = {} as { [key: string]: CoreV1Event[] }
    let workloadObj: KubernetesObject = {}

    // Action 1 Get Events of the Workload and All Abnormal Events
    const getEvents = async () => {
      const apiClient = kubeClient.makeApiClient(CoreV1Api)
      const abnormalEventsRes = await apiClient.listNamespacedEvent(namespace, undefined, undefined, undefined, 'type!=Normal')
      abnormalEventsRes.body.items
        .filter(e => e.involvedObject.name?.includes(workload))
        .sort((a, b) => (b.lastTimestamp?.valueOf() || 0) - (a.lastTimestamp?.valueOf() || 0))
        .forEach(event => {
          event.metadata.managedFields = undefined
          const key = (event as unknown as CoreV1Event).involvedObject.name || 'unknown'
          if (!abnormalEvents[key]) {
            abnormalEvents[key] = []
          }
          abnormalEvents[key].push(event as unknown as CoreV1Event)
        })
    }

    // Action 2 Get Workload and Check Status
    const getWorkloadAndStatus = async () => {
      const kind = (appModel.deploy as KV)?.type as string
      const workloadRes = await kubeClient.find({ kind, namespace }, { workload })
      if (workloadRes.length <= 0) {
        ready = 'Not Found'
      } else {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (workloadRes[0] as any).metadata.managedFields = undefined
          await kubeClient.checkWorkloadStatus(kind as CheckableWorkloadType, workload, 1, namespace, cluster, workloadRes[0] as KubernetesObject)
        } catch (ex) {
          ready = 'Not Ready'
          logger.warn(`${workload} is not ready`)
        }
      }
      workloadObj = workloadRes.length > 0 ? workloadRes[0] as KubernetesObject : {}
    }

    // Action 3 Get Pods
    let podStatus: V1Pod[] = []
    const getPods = async () => {
      podStatus = await kubeClient.getPods({ labelSelectors: { workload }, namespace })
      podStatus.forEach(pod => {
        if (pod.metadata?.managedFields) {
          pod.metadata.managedFields = undefined
        }
      })
    }

    logger.info(`start to get aggregated status of workload: ${workload} in cluster: ${cluster}`)
    await Promise.all([getEvents(), getWorkloadAndStatus(), getPods()])

    const result: AggregatedAppStatus = {
      ready,
      appModel,
      appliedResourcesList: yamMetadata.appliedResources,
      lastAppliedAt: yamMetadata.timestamp.toISOString(),
      workload: workloadObj,
      podStatus,
      abnormalEvents
    }
    logger.info(`fetched all status of app: '${app}' in namespace: '${namespace}'`)
    return result
  })

  function appendRealtimeLogs(taskDetail: TaskDetail, str: string) {
    taskDetail.logs.push(str)
    const sockets = RealtimeLogConnections.get(taskDetail.taskId)
    if (sockets && sockets.length > 0) {
      sockets.forEach(socket => {
        socket.send(str)
      })
    }
  }

  function notifyTaskStatus(taskDetail: TaskDetail) {
    const sockets = StatusConnections.get(taskDetail.taskId)
    if (sockets && sockets.length > 0) {
      sockets.forEach(socket => {
        socket.send(JSON.stringify(_.omit(taskDetail, "abort", "logs")))
      })
    }
    if (TASK_CALLBACK_URL) {
      const url = TASK_CALLBACK_URL.replace(':taskId', taskDetail.taskId)
      axiosClient.post(url, '').then((resp) => {
        logger.info(`task exited, notified ${url}, response: ${resp.status}, ${JSON.stringify(resp.data)}`)
      }).catch(e => {
        logger.error(`can not notify ${url}, status: ${e.status}, msg: ${e.message}, response: ${JSON.stringify(e.response?.data)}`)
      })
    }
  }

  async function planApply(request: FastifyRequest, reply: FastifyReply, mode: OperatorMode): Promise<void> {
    const taskId = uuid()
    logger.info(`plan request - ${taskId}, param: ${JSON.stringify(_.omit(request.body as KV || {}, 'kubeConfig'))}`)
    const { clusters, variables, appModel, version, skipDiff, filter, dryRun, forceRestart, kubeConfig, debug, operator, maxWait } = request.body as KV

    if (typeof clusters !== "string" || !clusters) {
      reply.code(400).send("clusters parameter missing, no deploy targets")
      return
    }
    if (typeof appModel !== "string" || !appModel) {
      reply.code(400).send("appModel parameter missing")
      return
    }
    checkTargetFilePath(appModel)
    const modelExists = await fsExist(appModel)
    if (!modelExists) {
      reply.code(400).send("appModel does not exist")
      return
    }
    if (mode === "apply-only") {
      throw new Error("not supported yet")
    }
    const runMode = mode === "plan-apply" ? "apply" : mode === "plan-only" ? "plan" : "uninstall"
    const args = [runMode, "--clusters", clusters, "--dir", appModel]
    if (version) {
      args.push("--version", version as string)
    }
    if (filter) {
      args.push("--filter", filter as string)
    }
    if (skipDiff) {
      args.push("--skip-diff")
    }
    if (dryRun) {
      args.push("--dry-run")
    }
    if (forceRestart) {
      args.push("--force-restart")
    }
    if (maxWait) {
      args.push("--wait", (maxWait as number).toString())
    }
    if (variables) {
      const valuesFrom = path.resolve(tmpValuesDir, taskId + '-values.yaml')
      await fsWrite(valuesFrom, yaml.dump(variables))
      args.push("--values-from", valuesFrom)
    }
    const taskDetail: TaskDetail = {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      taskId, finished: false, success: false, startTime: Date.now(), logs: [], operator: operator as string || 'unknown', abort: () => { },
    }
    EngineTaskMap.set(taskId, taskDetail)

    // fork process to plan or apply or install
    const forkEnv = JSON.parse(JSON.stringify(process.env))
    if (kubeConfig && typeof kubeConfig === "string") {
      // check context mapping for external kube-config, retrieving from body for compatibility, prefer auth header
      const k8sConf = deserializeKubeConfig(clusters, kubeConfig)
      logger.info(`authorization kube config data detected, use it as kubeConfig`)
      forkEnv.KUBECONFIG = yaml.dump(k8sConf)
    } else if (request.headers.authorization) {
      const k8sConf = deserializeKubeConfig(clusters, fetchKubeConfigFromHeader(request.headers.authorization))
      logger.info(`authorization header detected, use header as kubeConfig`)
      forkEnv.KUBECONFIG = yaml.dump(k8sConf)
    }
    if (debug) {
      forkEnv.DEBUG = true
    }
    // Use parent process colorize to avoid Linux color issue
    const stdoutStream = new Writable()
    stdoutStream._write = (chunk, _, callback) => {
      appendRealtimeLogs(taskDetail, chunk.toString())
      callback()
    }
    if (logger.isLevelEnabled('trace')) {
      logger.trace(`forking sub process. args are:\n${yaml.dump(args)}\nenv vars are:\n${yaml.dump(forkEnv)}}`)
    }
    const proc = fork(path.join(__dirname, '../bin/yam.js'), args, {
      env: forkEnv, silent: true
    })
    proc.stdout?.pipe(stdoutStream)
    proc.stderr?.pipe(stdoutStream)
    taskDetail.abort = () => {
      logger.info(`stop forked process: ${proc.pid}, cancel ${runMode} progress of taskId ${taskDetail.taskId}.`)
      proc.kill()
    }
    proc.on('error', (err) => {
      taskDetail.err = err.message
      taskDetail.finished = true
      taskDetail.success = false
      taskDetail.endTime = Date.now()
      logger.error(`${taskId} failed. caused by:`, err)
      notifyTaskStatus(taskDetail)
    })
    proc.on("exit", (code) => {
      taskDetail.finished = true
      taskDetail.success = true
      taskDetail.endTime = Date.now()
      if (code !== 0) {
        taskDetail.success = false
        taskDetail.err = "fail to sync application model"
      }
      logger.info(`Task ${taskId} finished. exit code: ${code}`)
      notifyTaskStatus(taskDetail)
    })
    logger.info(`plan request handled - ${taskId}`)
    reply.code(200).send(_.omit(taskDetail, "abort", "logs"))
  }

  return server
}

function startTaskRotationJob() {
  setInterval(() => {
    // archive the tasks
    EngineTaskMap.forEach((detail) => {
      if (Date.now() - detail.startTime > TASK_GC_THRESHOLD) {
        EngineTaskMap.delete(detail.taskId)
        RealtimeLogConnections.delete(detail.taskId)
      }
    })
  }, TASK_GC_PERIOD)
}

function deserializeKubeConfig(clusters: string, kubeConfig?: string) {
  if (!kubeConfig) {
    return
  }
  try {
    const k8sConf = yaml.load(kubeConfig) as KV
    splitAndTrimComma(clusters).forEach(c => {
      const cluster = trimAndLowerAndRemoveSlash(c)
      const ctxs = k8sConf.contexts as KV[]
      if (ctxs?.filter(ctx => ctx.name === cluster || ctx.name === lookupWildcardKeyObj(cluster, engineSettings.clientConf.contextPatternMapping)).length === 0) {
        // for single cluster mode - convert the kubeConfig context
        if (ctxs?.length === 1) {
          logger.info(`convert kube context name from '${ctxs[0].name}' to '${cluster}' to avoid kube-context mismatching.`)
          ctxs[0].name = cluster
          k8sConf['current-context'] = cluster
        } else {
          throw new Error(`kubeConfig is not correct, can not found kube context: ${cluster} in kubeConfig request param.`)
        }
      }
    })
    return k8sConf
  } catch (ex) {
    logger.error(`invalid kubeConfig from request: ${ex.message}`)
    throw new Error(`invalid kubeConfig`)
  }
}

export const startServer = async (port: number, bind?: string): Promise<void> => {
  if (bind && bind !== "127.0.0.1") {
    logger.warn(`The command line is designed to be ran on localhost ONLY! Binding to '${bind}' or Exposing the API may cause SECURITY issues!`)
    if (!SECURE_WORK_DIR) {
      logger.error(`please specify 'SECURE_SERVER_WORK_DIR' to avoid directory traversal, server won't be started.`)
      process.exit(-1)
    }
  }
  startTaskRotationJob()
  enableGracefulShutdown4Server()
  try {
    const server = await init()
    await server.listen({
      port,
      host: bind || process.env.ENGINE_BIND_ADDR as string || '127.0.0.1'
    })
    logger.info('server started.')
  } catch (err) {
    logger.error(err)
    process.exit(1)
  }
}

export type AppStatus = 'Ready' | 'Not Ready' | 'Not Found' | 'N/A'

export type AggregatedAppStatus = {
  ready: AppStatus,
  appModel: IApplicationModel
  workload: KubernetesObject
  podStatus: V1Pod[]

  appliedResourcesList: KubernetesObject[]

  abnormalEvents: { [key: string]: CoreV1Event[] },

  lastAppliedAt: string
}

export function fetchKubeConfigFromHeader(authHeader?: string): string | undefined {
  if (!authHeader) {
    return authHeader
  }
  const bearerPrefix = 'Bearer '
  if (authHeader.startsWith(bearerPrefix)) {
    authHeader = authHeader.substring(bearerPrefix.length)
  }
  return Buffer.from(authHeader, 'base64').toString()
}

/**
 * Check if SECURE_SERVER_WORK_DIR matches the final dir
 */
function checkTargetFilePath(workDir: string, filename?: string): void {
  if (SECURE_WORK_DIR) {
    const finalPath = path.join(workDir, filename || '').replace(/\\/g, '/')
    if (!finalPath.startsWith(SECURE_WORK_DIR)) {
      logger.error(`path ${finalPath} is not in secure working dir: ${SECURE_WORK_DIR}, skip request handling`)
      throw new Error(`invalid path, not in secure working directory.`)
    }
  }
}


function enableGracefulShutdown4Server() {
  const CHECK_TASK_PERIOD = 3000
  process.once('SIGTERM', waitTaskAndExit)
  process.once('SIGINT', waitTaskAndExit)
  function waitTaskAndExit() {
    setInterval(() => {
      let allFinished = true
      EngineTaskMap.forEach((v) => {
        if (!v.finished) {
          allFinished = false
        }
      })
      // only when all tasks has been finished, it could be exit
      if (allFinished) {
        logger.info(`all tasks complete, server ready to exit.`)
        process.exit(0)
      } else {
        logger.info(`some tasks still work in progressing, waiting util task finished.`)
      }
    }, CHECK_TASK_PERIOD)
  }
}
